import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { apiCall } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface ProviderOption {
  id: string;
  name: string;
}

interface MoveAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMoved: () => void;
  appointmentId: string | null;
  providers: ProviderOption[];
  initialProviderId: string;
  initialStartTime: Date | null;
  initialDurationMinutes: number;
}

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toTimeInputValue = (date: Date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const formatTime = (date: Date) =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

const combineDateAndTime = (dateStr: string, timeStr: string): Date | null => {
  if (!dateStr || !timeStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);
  if ([year, month, day, hours, minutes].some((n) => Number.isNaN(n))) return null;
  return new Date(year, month - 1, day, hours, minutes);
};

// Repositions an existing appointment in time/provider without touching
// anything else about it (patient, type, notes, duration) -- distinct from
// the full Edit flow. Used by both the right-click "Move Appointment"
// context menu item and the QuickInfo popup's "Move" button.
const MoveAppointmentModal: React.FC<MoveAppointmentModalProps> = ({
  isOpen,
  onClose,
  onMoved,
  appointmentId,
  providers,
  initialProviderId,
  initialStartTime,
  initialDurationMinutes,
}) => {
  const { token } = useAuth();
  const [providerId, setProviderId] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setProviderId(initialProviderId);
    const seed = initialStartTime || new Date();
    setDate(toDateInputValue(seed));
    setStartTime(toTimeInputValue(seed));
  }, [isOpen, initialProviderId, initialStartTime]);

  if (!isOpen) return null;

  const newStart = combineDateAndTime(date, startTime);
  const newEnd = newStart ? new Date(newStart.getTime() + initialDurationMinutes * 60000) : null;

  const handleSubmit = async () => {
    if (!appointmentId || !newStart || !newEnd || !providerId) {
      setError('Please select a provider, date, and time.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const response = await apiCall(`/scheduling/appointments/${appointmentId}/`, {
        method: 'PATCH',
        body: JSON.stringify({
          provider: providerId,
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString(),
          // The appointment may no longer belong to whichever session/room
          // it was originally linked to once moved -- clear it rather than
          // leave it silently pointing at a now-unrelated session.
          provider_session: null,
        }),
      }, token || undefined);

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || body.error || 'Failed to move appointment');
      }

      onMoved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move appointment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Move Appointment</h3>
          <button className="btn btn-sm btn-circle btn-ghost" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-4">
          <div className="form-control">
            <label className="label"><span className="label-text">Provider *</span></label>
            <select
              className="select select-bordered w-full"
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
            >
              <option value="">Select provider</option>
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>{provider.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label"><span className="label-text">Date *</span></label>
              <input
                type="date"
                className="input input-bordered w-full"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text">Start Time *</span></label>
              <input
                type="time"
                className="input input-bordered w-full"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
          </div>

          {newEnd && (
            <div className="text-sm opacity-70">
              Duration unchanged ({initialDurationMinutes} min) -- ends at {formatTime(newEnd)}
            </div>
          )}
        </div>

        <div className="modal-action">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !newStart || !providerId}
          >
            {submitting ? 'Moving...' : 'Move Appointment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveAppointmentModal;
